import { useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  MdContentCopy, MdDownload, MdImage, MdPictureAsPdf,
  MdCode, MdCheckCircle, MdOpenInNew,
} from 'react-icons/md';
import { FaWhatsapp, FaFacebook, FaInstagram, FaEnvelope } from 'react-icons/fa';
import { getMarketingAssets } from '../../services/clubService';
import { useTenant } from '../../context/TenantContext';
import useTheme from '../../context/ThemeContext';
import useClubVocab from '../../hooks/useClubVocab';

// Shared dark "chip" button style for all non-primary actions.
const chipCls = 'inline-flex items-center justify-center gap-2 h-[38px] px-3 rounded-full border border-divider bg-card text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 transition-all duration-200 disabled:opacity-50';

function CopyField({ value }) {
  const currentTheme = useTheme()?.currentTheme;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 bg-nav-hover/40 border border-divider rounded-full pl-3 pr-1.5 h-[42px]">
      <span className="flex-1 min-w-0 text-xs text-secondary truncate font-mono">{value}</span>
      <button
        onClick={copy}
        className={`flex-shrink-0 inline-flex items-center justify-center gap-1 h-8 px-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-xs font-medium hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 transition-all duration-200`}
      >
        {copied ? <MdCheckCircle className="text-base" /> : <MdContentCopy className="text-base" />}
        <span>{copied ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
  );
}

export default function Marketing() {
  const { club } = useTenant();
  const v        = useClubVocab();
  const qrRef    = useRef();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);

  const { data: assets, isLoading } = useQuery({
    queryKey: ['marketing-assets'],
    queryFn:  () => getMarketingAssets().then(r => r.data),
  });

  const trialUrl  = assets?.trialUrl  || '';
  const embedCode = assets?.embedCode || '';

  // ── Share handlers ─────────────────────────────────────────────────────────
  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Join ${club?.name} for a FREE trial class! Register here: ${trialUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareFacebook = () => {
    const url = encodeURIComponent(trialUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`Free Trial Class at ${club?.name}`);
    const body = encodeURIComponent(
      `Hi!\n\nI wanted to let you know about a FREE trial class opportunity at ${club?.name}.\n\nRegister here: ${trialUrl}\n\nSee you on the mat!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareInstagram = () => {
    navigator.clipboard.writeText(trialUrl);
    toast.success('Link copied — paste it in your Instagram bio or story!');
  };

  // ── QR download ────────────────────────────────────────────────────────────
  const downloadQR = useCallback(() => {
    const svg    = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgStr     = serializer.serializeToString(svg);
    const canvas     = document.createElement('canvas');
    const size       = 400;
    canvas.width     = size;
    canvas.height    = size;
    const ctx        = canvas.getContext('2d');
    const img        = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const link  = document.createElement('a');
      link.download = `${club?.slug || 'club'}-trial-qr.png`;
      link.href    = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
  }, [club]);

  // ── Promo image (canvas) ────────────────────────────────────────────────────
  const generatePromoImage = useCallback(async () => {
    setPromoLoading(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const promoEl = document.getElementById('promo-card');
      if (!promoEl) return;
      const canvas = await html2canvas(promoEl, { scale: 2, useCORS: true, backgroundColor: null });
      const link = document.createElement('a');
      link.download = `${club?.slug || 'club'}-promo.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Promo image downloaded!');
    } catch (err) {
      toast.error('Failed to generate image');
    } finally {
      setPromoLoading(false);
    }
  }, [club]);

  // ── PDF flyer ──────────────────────────────────────────────────────────────
  const generatePDF = useCallback(async () => {
    setPdfLoading(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');

      const promoEl = document.getElementById('promo-card');
      if (!promoEl) return;

      const canvas  = await html2canvas(promoEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');

      const pdf  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
      const w    = pdf.internal.pageSize.getWidth();
      const h    = (canvas.height / canvas.width) * w;
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);

      // QR code section below
      const svgEl = qrRef.current?.querySelector('svg');
      if (svgEl) {
        const serializer = new XMLSerializer();
        const svgStr     = serializer.serializeToString(svgEl);
        const qrCanvas   = document.createElement('canvas');
        qrCanvas.width   = 200;
        qrCanvas.height  = 200;
        const ctx = qrCanvas.getContext('2d');
        const img = new Image();
        await new Promise(resolve => {
          img.onload = () => {
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, 200, 200);
            ctx.drawImage(img, 0, 0, 200, 200);
            resolve();
          };
          img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
        });
        const qrY = h + 4;
        pdf.setFontSize(9);
        pdf.setTextColor(100);
        pdf.text('Scan to register for a free trial:', w / 2, qrY + 2, { align: 'center' });
        pdf.addImage(qrCanvas.toDataURL('image/png'), 'PNG', (w - 30) / 2, qrY + 4, 30, 30);
      }

      pdf.save(`${club?.slug || 'club'}-flyer.pdf`);
      toast.success('Flyer downloaded!');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [club]);

  const brandColor = club?.primaryColor || '#E24B4A';
  const clubName   = club?.name || 'Your Club';
  const tagline    = club?.tagline || 'Join us for a FREE trial class!';

  if (isLoading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-16">
      {/* 1 — Page header */}
      <div>
        <h1 className="text-lg font-medium text-secondary">Grow your club</h1>
        <p className="text-[13px] text-muted mt-0.5">Share your free-trial link and bring in new {v.members.toLowerCase()}.</p>
      </div>

      {/* 2 — Hero card: trial link + share (left) · QR (right) */}
      <div className="bg-card border border-divider rounded-2xl shadow-sm p-5">
        <div className="flex flex-col md:flex-row gap-6">
          {/* LEFT */}
          <div className="flex-1 min-w-0 space-y-5">
            {/* Trial link */}
            <div>
              <p className="text-xs font-medium text-muted mb-1.5">Your trial sign-up link</p>
              <CopyField value={trialUrl} />
              <a
                href={trialUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-muted hover:text-secondary transition-colors"
              >
                <MdOpenInNew className="text-sm" /> Preview
              </a>
            </div>

            {/* Share */}
            <div>
              <p className="text-xs font-medium text-muted mb-1.5">Share it</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={shareWhatsApp} className={chipCls}>
                  <FaWhatsapp className="text-base text-[#25D366]" /> WhatsApp
                </button>
                <button onClick={shareFacebook} className={chipCls}>
                  <FaFacebook className="text-base text-[#1877F2]" /> Facebook
                </button>
                <button onClick={shareInstagram} className={chipCls}>
                  <FaInstagram className="text-base text-[#E1306C]" /> Instagram
                </button>
                <button onClick={shareEmail} className={chipCls}>
                  <FaEnvelope className="text-base text-muted" /> Email
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT — QR (white tile kept literal so it stays scannable) */}
          <div className="flex flex-col items-center gap-3">
            <div ref={qrRef} className="p-3 bg-white rounded-xl shadow-sm">
              <QRCodeSVG
                value={trialUrl || 'https://trainaxis.com'}
                size={160}
                fgColor={brandColor}
                bgColor="#ffffff"
                level="M"
                includeMargin={false}
              />
              <p className="text-[10px] text-center text-gray-400 mt-2 font-medium">Scan to register</p>
            </div>
            <button onClick={downloadQR} className={chipCls}>
              <MdDownload className="text-base" /> Download QR Code (PNG)
            </button>
          </div>
        </div>
      </div>

      {/* 3 — Bottom row: promo material · embed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Promo material */}
        <div className="bg-card border border-divider rounded-2xl shadow-sm p-[18px]">
          <h2 className="font-semibold text-secondary flex items-center gap-2">
            <MdImage className="text-primary" /> Promo material
          </h2>
          <p className="text-[13px] text-muted mt-1 mb-4">Download a printable flyer or a social-ready image.</p>

          {/* Preview card — EXPORT-BOUND (captured by html2canvas); styling left literal */}
          <div
            id="promo-card"
            className="w-full max-w-sm rounded-2xl overflow-hidden shadow-lg border border-gray-100 mb-4"
            style={{ fontFamily: 'sans-serif' }}
          >
            {/* Cover */}
            <div
              className="h-32 flex flex-col items-center justify-center relative"
              style={{ backgroundColor: brandColor }}
            >
              {club?.coverPhotoUrl && (
                <img
                  src={club.coverPhotoUrl}
                  alt="cover"
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                  crossOrigin="anonymous"
                />
              )}
              <div className="relative z-10 text-center px-4">
                {club?.logo && (
                  <img
                    src={club.logo}
                    alt="logo"
                    className="w-12 h-12 rounded-full object-cover mx-auto mb-1 border-2 border-white/50"
                    crossOrigin="anonymous"
                  />
                )}
                <p className="text-white font-bold text-lg leading-tight">{clubName}</p>
              </div>
            </div>
            {/* Body */}
            <div className="bg-white px-5 py-4 text-center">
              <p className="text-xl font-bold text-gray-800 mb-0.5">FREE Trial Class!</p>
              <p className="text-sm text-gray-500 mb-3">{tagline}</p>
              <div
                className="text-white text-xs font-semibold px-4 py-2 rounded-lg inline-block"
                style={{ backgroundColor: brandColor }}
              >
                Register Online Now
              </div>
              <p className="text-[10px] text-gray-400 mt-2 break-all">{trialUrl}</p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={generatePromoImage} disabled={promoLoading} className={chipCls}>
              {promoLoading
                ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <MdImage className="text-base" />}
              Download Promo Image
            </button>
            <button onClick={generatePDF} disabled={pdfLoading} className={chipCls}>
              {pdfLoading
                ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <MdPictureAsPdf className="text-base" />}
              Download PDF Flyer
            </button>
          </div>
        </div>

        {/* Embed */}
        <div className="bg-card border border-divider rounded-2xl shadow-sm p-[18px]">
          <h2 className="font-semibold text-secondary flex items-center gap-2">
            <MdCode className="text-primary" /> Embed on your website
          </h2>
          <p className="text-[13px] text-muted mt-1 mb-3">
            Paste this snippet into your site to embed the trial registration form directly.
          </p>
          <CopyField value={embedCode} />
        </div>
      </div>
    </div>
  );
}
